import { apiClient } from '../client/apiClient';
import { FrontendLogOptions } from '../client/types/FrontendLogOptions';
import { RecordPlayerLogRequestType } from '../common/dtos/internal/recordPlayerLog';

/**
 * Log a frontend event by sending it to the API
 *
 * Frontend cannot log directly (no Winston instance in browser).
 * This helper sends events to the API which logs them server-side.
 *
 * @param options - Event type and metadata
 * @param options.eventType - The event from FrontendSharedEvents (type-safe)
 * @param options.[key] - Additional metadata (error details, context, etc.)
 *
 * @example
 * // Widget crash
 * void logFrontendEventApi({
 *   eventType: FrontendSharedEvents.error.widgetCrash,
 *   widgetType: 'TextInput',
 *   contentObjectId: widget.id,
 *   serializedError: serializeError(error),
 *   studentAnswer: widget.currentValue
 * });
 *
 * @example
 * // Performance issue
 * void logFrontendEventApi({
 *   eventType: FrontendSharedEvents.performance.slowRender,
 *   componentName: 'LearningActivityPage',
 *   renderTime: 2150,
 *   widgetCount: widgets.length
 * });
 *
 * @example
 * // Autosave failure
 * void logFrontendEventApi({
 *   eventType: FrontendSharedEvents.player.autosaveFailed,
 *   learningObjectDraftId,
 *   consecutiveFailures: 3,
 *   unsavedChanges: getUnsavedCount()
 * });
 */
export const logFrontendEventApi = async (options: FrontendLogOptions): Promise<void> => {
  const { eventType, ...data } = options;

  try {
    await apiClient.POST('/internal/player-logs', {
      body: {
        ...data,
        eventType: eventType as string,
        url: window.location.href
      } as RecordPlayerLogRequestType
    });
  } catch (error) {
    // Silent fail - don't break the app if logging fails
    // Use console.error so it's visible in dev tools but doesn't crash the app
    console.error('Failed to log frontend event:', error);
  }
};
